import { RampStore } from '../internal/stores/RampStore';
import { NOT_FOUND_ROUTE, getAppRoutes } from '../routes';
import { getDecorators } from '@estee/elc-service';
import { RouterStore } from '@estee/elc-router';
import { Container } from 'hypo-container';
import { ServiceBusController } from './ServiceBusController';
import { BrowserStorage, root } from '@estee/elc-universal-utils';
import { ConfigRepository } from '../internal/repositories/ConfigRepository';
import { UserRepository } from '../internal/repositories/UserRepository';
import { SignUpViewController } from '../internal/controllers/signup/SignUpViewController';
import { LoginViewController } from '../internal/controllers/login/LoginViewController';
import { QRCodeViewController } from '../internal/controllers/qrcode/QRCodeViewController';
import { RampService } from './RampService';
import { RampSdApiSdk } from '../api/RampSdApiSdk';
import { ELCLogger } from '@estee/elc-logging';
import { DataMapper, HttpTransport } from '@estee/elc-http-transport';
import apiDataMapping from '../api/DataMappings';
import { RampPgApiSdk } from '../api/RampPgApiSdk';
import { TranslationRepository } from '../internal/repositories/TranslationRepository';
import { DropdownDataStore } from '../internal/repositories/DropdownDataStore';
import { RampViewController } from '../internal/controllers/RampViewController';
import { IRampAppConfig } from '../internal/domain/entities/Config';

export const serviceNames = {
    rampService: 'RAMP_SERVICE',
    routerStore: 'ROUTER_STORE',
    serviceBusController: 'SERVICE_BUS_CONTROLLER',
    rampStore: 'RAMP_STORE',
    configRepository: 'CONFIG_REPOSITORY',
    translationRepository: 'TRANSLATION_REPOSITORY',
    dropdownDataStore: 'DROPDOWN_DATA_STORE',
    logger: 'LOGGER',
    rampViewController: 'RAMP_VIEW_CONTROLLER',
    signUpViewController: 'SIGN_UP_VIEW_CONTROLLER',
    loginViewController: 'LOGIN_IN_VIEW_CONTROLLER',
    qrCodeViewController: 'QR_CODE_VIEW_CONTROLLER',
    rampSdApiSdk: 'RAMP_SD_API_SDK',
    rampPgApiSdk: ' RAMP_PG_API_SDK',
    httpTransport: 'HTTP_TRANSPORT',
    userRepository: 'USER_REPOSITORY',
    apiDataMapper: 'API_DATA_MAPPER'
};

const { name: LOGGER_SERVICE_NAME } = __serviceInfo__;

export const diContainer = new Container();
export const setConfig = (configJSON: IRampAppConfig) => {
    const { businessUnitId, pgApiTokens } = configJSON;
    diContainer.config = { businessUnitId, pgApiTokens };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setDrupalConfig = (configJSON: any) => {
    diContainer.drupalConfig = configJSON;
};

export const setNewDiContainer = (container: Container) => {
    container
        .register(serviceNames.apiDataMapper, () => new DataMapper(apiDataMapping))
        .register(serviceNames.httpTransport, () => {
            return root.HttpTransport;
        })
        .register(
            serviceNames.rampService,
            (c) =>
                new RampService((config: IRampAppConfig) => {
                    setConfig(config);

                    return c.get(serviceNames.serviceBusController);
                })
        )
        .register(serviceNames.routerStore, (c) => {
            const configRepository = c.get(serviceNames.configRepository);

            const appRoutes = getAppRoutes(configRepository.config.config.routes);

            return new RouterStore(appRoutes, NOT_FOUND_ROUTE);
        })
        .register(
            serviceNames.rampStore,
            (c) =>
                new RampStore({
                    routerStore: c.get(serviceNames.routerStore),
                    persistentStorage: BrowserStorage
                })
        )
        .register(serviceNames.serviceBusController, () => new ServiceBusController())
        .register(serviceNames.configRepository, (c) => new ConfigRepository(c.config))
        .register(serviceNames.translationRepository, (c) => new TranslationRepository())
        .register(serviceNames.dropdownDataStore, () => new DropdownDataStore())
        .register(serviceNames.logger, () => new ELCLogger({ serviceName: LOGGER_SERVICE_NAME }))
        .register(serviceNames.rampSdApiSdk, (c) => {
            const configRepository = c.get(serviceNames.configRepository);
            const { businessUnitId } = configRepository.config.config;
            const logger = c.get(serviceNames.logger);

            return new RampSdApiSdk({
                businessUnitId,
                httpTransport: c.get(serviceNames.httpTransport),
                logger
            });
        })
        .register(serviceNames.rampPgApiSdk, (c) => {
            return new RampPgApiSdk(
                c.get(serviceNames.logger),
                new HttpTransport(),
                c.get(serviceNames.apiDataMapper)
            );
        })
        .register(serviceNames.userRepository, (c) => {
            const { signUpFormInitialValues } = c.get(serviceNames.configRepository);

            return new UserRepository(signUpFormInitialValues);
        })
        .register(serviceNames.rampViewController, () => new RampViewController())
        .register(serviceNames.signUpViewController, () => new SignUpViewController())
        .register(serviceNames.loginViewController, () => new LoginViewController())
        .register(serviceNames.qrCodeViewController, () => new QRCodeViewController());
};
setNewDiContainer(diContainer);

const { lazyInject } = getDecorators(diContainer);

export { lazyInject };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServiceBusController = (config: any) => {
    setDrupalConfig(config);
};
